import TrashCan from "@assets/images/trash.svg";
import {
  Backdrop,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import DecimalFormat from "@saleor/components/DecimalFormat";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat/NumberFormat";
import SnackbarAlert from "@saleor/components/SnackbarAlert";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { DrawAddExportType } from "@saleor/createOrderExport/const";
import EmptyProduct from "@saleor/createOrderExport/views/CartDetailExport/EmptyProduct";
import { TypeOfAddProd } from "@saleor/createOrderExport/views/CreateOrderView";
import {
  ExportCartItemInput,
  GetCartDetailDataQuery,
  useDeleteExportCartItemsMutation,
  useTotalCountExportCartQuery,
  useUpdateExportCartMutation
} from "@saleor/graphql";
import { renderCollection } from "@saleor/misc";
import { ListProps, RelayToFlat } from "@saleor/types";
import { convertToRolExport } from "@saleor/utils/data/convertUnit";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import DrawAddProduct from "../DrawAddProduct";
import BottomBar from "./BottomBar";
import { useStyles } from "./style";

interface ICartDetailTableProps extends ListProps {
  onBack?: () => void;
  onSubmitCart: (data) => void;
  disabled: any;
  refetch: any;
  data: RelayToFlat<GetCartDetailDataQuery["exportCart"]["items"]>;
  settings: any;
  onUpdateListSettings: any;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  dataInfo: {
    piNo: string;
    piId: string;
    soldTo: string;
    checkOutId: string;
  };
  currency: string;
  handleUpdateExportCart: (itemCart) => void;
}

interface dataDialogDeleteProps {
  title: string | React.ReactNode;
  content: string | React.ReactNode;
  lines: string[];
}

const CartDetailTable: React.FC<ICartDetailTableProps> = props => {
  const {
    data,
    refetch,
    settings,
    totalCount,
    pageInfo,
    disabled,
    latestPageItemNumber,
    onUpdateListSettings,
    onSubmitCart,
    onNextPage,
    onNextLastPage,
    onPreviousPage,
    onPreviousFirstPage,
    dataInfo,
    currency,
    loading: loadingProps,
    handleUpdateExportCart
  } = props;

  const [listLineState, setListLineState] = useState<any[]>([]);
  const [listLineChange, setListLineChange] = useState<any[]>([]);
  const [openConfrmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openDrawAddProduct, setOpenDrawAddProduct] = useState<boolean>(false);
  const [dataDialogDelete, setDataDialogDelete] = useState<
    dataDialogDeleteProps
  >({
    title: "",
    content: "",
    lines: []
  });
  const [isSuccessfully, setSuccessfully] = useState<TypeOfAddProd | null>(
    null
  );
  const [componentKey, setComponentKey] = useState<number>(0);
  const { refetch: refetchCart } = useTotalCountExportCartQuery({
    skip: true
  });
  const [deleteContractLines] = useDeleteExportCartItemsMutation({
    onCompleted: data => {
      if (!data?.deleteExportCartItems?.errors?.length) {
        refetch();
        setListLineChange([]);
        refetchCart();
        onPreviousFirstPage();
      }
    }
  });

  const intl = useIntl();

  const classes = useStyles();

  useEffect(() => {
    if (data) {
      const listItem = data.map(item => ({
        ...item,
        selected: true,
        errorQuantity: false
      }));

      listItem.forEach(line => {
        const lineChange = listLineChange.find(change => change.id === line.id);
        if (lineChange) {
          line.selected = lineChange.selected;
          line.errorQuantity = lineChange.errorQuantity;
          line.quantity = lineChange.quantity;
        }
      });
      setListLineState(listItem);
    }
  }, [JSON.stringify(data)]);

  const getSelectedLine = () => listLineState.filter(item => item.selected);

  const getCheckedHeader = () => listLineState.every(item => item.selected);

  const getIndeterminateHeader = () => {
    const listSelectedLength = getSelectedLine().length;
    return listSelectedLength > 0 && listSelectedLength < listLineState.length;
  };

  const confirmDelete = async () => {
    setLoading(true);
    setOpenConfirmDelete(false);
    try {
      await deleteContractLines({
        variables: {
          cartItemIds: dataDialogDelete.lines
        }
      });
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const [handleAddProdToCart] = useUpdateExportCartMutation({
    onCompleted: () => {
      refetch();
      refetchCart();
      setSuccessfully(TypeOfAddProd.Successfully);
    },
    onError: () => {
      setSuccessfully(TypeOfAddProd.UnSuccessfully);
    }
  });

  const onClickCheckHeader = () => {
    const lines = [...listLineState];
    const linesTemp = [...listLineChange];
    const listSelected = getSelectedLine();
    if (listSelected.length < listLineState.length) {
      lines.forEach(item => {
        item.selected = true;
        if (item.quantity) {
          const remain =
            (item.piProduct?.salesUnit === "EA"
              ? item.piProduct?.remainingQuantityEx
              : item.piProduct?.remainingQuantityEx / item.piProduct?.weight) ||
            0;
          item.errorQuantity = item.quantity > remain;
        } else {
          item.errorQuantity = true;
        }
      });
    } else if (listSelected.length === listLineState.length) {
      lines.forEach(item => {
        item.selected = false;
        item.errorQuantity = false;
        const lineTemp = linesTemp.find(temp => temp.id === item.id);
        if (lineTemp) {
          lineTemp.selected = false;
        }
      });
    }
    setListLineState(lines);
    setListLineChange(linesTemp);
  };

  const onChangeCheckItem = (lineId: string, event) => {
    const lines = [...listLineState];

    const lineChange = lines.find(item => item.id === lineId);

    const checked = event.target.checked;

    lineChange.selected = checked;
    const remain =
      (lineChange.piProduct?.salesUnit === "EA"
        ? lineChange.piProduct?.remainingQuantityEx
        : lineChange.piProduct?.remainingQuantityEx /
          lineChange.piProduct?.weight) || 0;
    if (!lineChange.quantity) {
      lineChange.errorQuantity = checked;
    } else {
      if (lineChange.quantity > remain) {
        lineChange.errorQuantity = checked;
      }
    }
    setListLineState(lines);
    //
    const linesTemp = [...listLineChange];
    const lineTempChange = linesTemp.find(item => item.id === lineId);
    if (lineTempChange) {
      lineTempChange.selected = checked;
    }
    setListLineChange(linesTemp);
  };

  const onChangeQuantity = (lineId, event) => {
    const lines = [...listLineState];
    const lineChange = lines.find(line => line.id === lineId);
    const remain =
      (lineChange.piProduct?.salesUnit === "EA"
        ? lineChange.piProduct?.remainingQuantityEx
        : lineChange.piProduct?.remainingQuantityEx /
          lineChange.piProduct?.weight) || 0;
    const quantity = event.target.value;
    if (quantity) {
      lineChange.selected = true;
    }
    lineChange.quantity = quantity;
    if (lineChange.selected) {
      if (quantity) {
        lineChange.errorQuantity = quantity > remain;
      } else {
        lineChange.errorQuantity = true;
      }
    } else {
      lineChange.errorQuantity = false;
    }
    setListLineState(lines);
  };

  const textError = intl.formatMessage({
    defaultMessage: "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ"
  });

  if (!data) {
    return null;
  }

  const getDisableSubmit = () => {
    const listSelected = getListAll(true);

    if (listSelected?.length <= 0) {
      return true;
    }

    return false;
  };
  const setDeleteSingle = lineId => {
    setDataDialogDelete({
      lines: [lineId],
      title: "ลบรายการนี้",
      content: "คุณกำลังจะลบรายการสินค้านี้"
    });
    setOpenConfirmDelete(true);
  };

  const setDeleteSelected = () => {
    const lineSelected = listSelectedAll.map(line => line.id);
    const content = (
      <span>
        คุณกำลังจะลบสินค้า <b>{lineSelected.length}</b> รายการ ในตะกร้านี้
      </span>
    );
    setDataDialogDelete({
      lines: lineSelected,
      title: "ลบรายการสินค้าที่เลือก",
      content
    });
    setOpenConfirmDelete(true);
  };

  const createOrder = () => {
    const listSelected = listSelectedAll.filter(item => item.selected);
    onSubmitCart(listSelected);
  };

  const handleAddProd = values => {
    const params: ExportCartItemInput[] = values.map(item => ({
      piProductId: item.id,
      quantity: item.quantity
    }));

    handleAddProdToCart({
      variables: {
        id: dataInfo?.checkOutId,
        input: {
          lines: params
        }
      }
    });
  };
  const handleClose = () => {
    setOpenDrawAddProduct(false);
  };
  const beforeChangePage = () => {
    const listSelected = listLineState.filter(line => line.selected);
    const listChangeTemp = [...listLineChange];
    if (listSelected.length) {
      listSelected.forEach(itemTouch => {
        const indexThisItemInListChange = listChangeTemp.findIndex(
          itemChange => itemChange.id === itemTouch.id
        );
        if (indexThisItemInListChange >= 0) {
          listChangeTemp[indexThisItemInListChange] = itemTouch;
        } else {
          listChangeTemp.push(itemTouch);
        }
      });
    }
    setListLineChange(listChangeTemp);
  };

  const handleNextPage = () => {
    beforeChangePage();
    onNextPage();
  };

  const handlePreviousPage = () => {
    beforeChangePage();
    onPreviousPage();
  };

  const handleNextLastPage = () => {
    beforeChangePage();
    onNextLastPage();
  };

  const handlePreviousFirstPage = () => {
    beforeChangePage();
    onPreviousFirstPage();
  };

  const getListAll = (countSelected = false) => {
    const listSelectedFromState = countSelected
      ? listLineState.filter(item => item.selected)
      : listLineState;
    const listIdSelectedState = listSelectedFromState.map(item => item.id);
    const listNotDuplicate = listLineChange
      .filter(item => (countSelected ? item.selected : true))
      .filter(item => {
        const indexOf = listIdSelectedState.indexOf(item.id);
        return indexOf < 0;
      });
    return [...listSelectedFromState, ...listNotDuplicate];
  };

  const handleOpenDrawAddProduct = () => {
    setComponentKey(prev => prev + 1);
    setOpenDrawAddProduct(true);
  };

  const onBlurQuantityItemCart = itemCart => {
    handleUpdateExportCart(itemCart);
  };

  const listSelectedAll = getListAll(true);

  return (
    <>
      <div className={classes.tableProduct}>
        <div className={classes.tableTitle}>
          <MyTypography variant={"h7"}>
            <FormattedMessage defaultMessage={"รายการสินค้า "} />({totalCount})
          </MyTypography>
        </div>
        {listLineState?.length ? (
          <>
            <div className={classes.root} ref={containerRef}>
              <Table className={classes.mainTable}>
                <TableHead>
                  <TableRow>
                    <TableCellHeader style={{ minWidth: 50 }}>
                      <Checkbox
                        size={"small"}
                        checked={getCheckedHeader()}
                        indeterminate={getIndeterminateHeader()}
                        onClick={onClickCheckHeader}
                        name="checkbox-header"
                        id="checkbox-header"
                      />
                    </TableCellHeader>
                    <TableCellHeader style={{ minWidth: 400 }}>
                      <FormattedMessage defaultMessage={"รายละเอียดสินค้า"} />
                    </TableCellHeader>
                    <TableCellHeader style={{ minWidth: 220 }} textAlign="left">
                      <FormattedMessage defaultMessage={"จำนวนสั่งซื้อ"} />
                    </TableCellHeader>
                    <TableCellHeader style={{ minWidth: 100 }}>
                      <FormattedMessage defaultMessage={"หน่วย"} />
                    </TableCellHeader>
                    <TableCellHeader
                      style={{ minWidth: 100 }}
                      textAlign="center"
                    >
                      <FormattedMessage defaultMessage={"ลบ"} />
                    </TableCellHeader>
                    <TableCellHeader
                      style={{ minWidth: 130 }}
                      className={classNames(classes.cellBackground)}
                      textAlign="right"
                    >
                      {intl.formatMessage({ defaultMessage: "จำนวน" })}
                    </TableCellHeader>
                    <TableCellHeader
                      style={{ minWidth: 120 }}
                      className={classNames(classes.cellBackground)}
                      textAlign="center"
                    >
                      {intl.formatMessage({ defaultMessage: "หน่วย" })}
                    </TableCellHeader>
                    <TableCellHeader
                      style={{ minWidth: 130 }}
                      className={classNames(classes.cellBackground)}
                      textAlign="right"
                    >
                      {intl.formatMessage({ defaultMessage: "ราคา/หน่วย" })}
                    </TableCellHeader>
                    <TableCellHeader
                      style={{ minWidth: 130 }}
                      className={classNames(classes.cellBackground)}
                      textAlign="right"
                    >
                      <FormattedMessage defaultMessage={"Currency"} />
                    </TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderCollection(listLineState, item => (
                    <TableRow className={classes.tableRow} key={item.id}>
                      <TableCell>
                        <Checkbox
                          size={"small"}
                          checked={item.selected}
                          onChange={e => onChangeCheckItem(item.id, e)}
                          id={`checkbox-${item.id}`}
                        />
                      </TableCell>
                      <TableCell className={classes.colNumber}>
                        {item ? item.piProduct.product.name : <Skeleton />}
                      </TableCell>
                      <TableCell>
                        <NumberFormat
                          placeholder={"ระบุจำนวน"}
                          onChange={e => onChangeQuantity(item.id, e)}
                          value={item.quantity}
                          error={item.errorQuantity}
                          decimalScale={0}
                          allowLeadingZeros
                          onBlur={() => onBlurQuantityItemCart(item)}
                          id={`quantity-${item.id}`}
                        />
                        {item.errorQuantity && item.quantity ? (
                          <div className={classes.textError}>{textError}</div>
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell className={classes.colProductName}>
                        {item ? (
                          convertToRolExport(item.piProduct?.salesUnit)
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align={"center"}>
                        <SVG
                          src={TrashCan}
                          cursor="pointer"
                          onClick={() => setDeleteSingle(item.id)}
                          id={`delete-${item.id}`}
                        />
                      </TableCell>
                      <TableCell
                        className={classNames(classes.cellBackground)}
                        align="right"
                      >
                        {item ? (
                          <DecimalFormat
                            value={item.piProduct.totalQuantity}
                            isGrayDecimal
                            countDecimal={3}
                          />
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={classNames(classes.cellBackground)}
                      >
                        <MyTypography variant="body3">
                          {item.piProduct?.weightUnit}
                        </MyTypography>
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classNames(classes.cellBackground)}
                      >
                        {item ? (
                          item.piProduct.pricePerUnit ? (
                            <DecimalFormat
                              value={item.piProduct.pricePerUnit}
                              countDecimal={2}
                            />
                          ) : (
                            ""
                          )
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classNames(classes.cellBackground)}
                      >
                        {item.piProduct.pricePerUnit ? (
                          <MyTypography variant="body3">
                            {currency ? currency : "USD"}
                          </MyTypography>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {listLineState?.length ? (
                <StickyHorizontalScrollBar container={containerRef} />
              ) : null}
            </div>
            <div className={classes.paginationWrapper}>
              <TablePaginationCustom
                settings={settings}
                hasNextPage={
                  pageInfo && !disabled ? pageInfo.hasNextPage : false
                }
                onNextPage={handleNextPage}
                onPreviousFirstPage={handlePreviousFirstPage}
                onNextLastPage={handleNextLastPage}
                onUpdateListSettings={onUpdateListSettings}
                hasPreviousPage={
                  pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                }
                onPreviousPage={handlePreviousPage}
                latestPageItemNumber={latestPageItemNumber}
                totalCount={totalCount}
                selectColor="#007E92"
                isBackFistPage
                isNextLastPage
                disabled={disabled}
              />
            </div>
          </>
        ) : (
          <EmptyProduct />
        )}
      </div>

      <BottomBar
        selectedLine={listSelectedAll.length}
        disabled={getDisableSubmit() || loading || loadingProps}
        onClick={handleOpenDrawAddProduct}
        onSubmit={createOrder}
        onDeleteClick={setDeleteSelected}
      />
      <DialogConfirmDelete
        onConfirm={confirmDelete}
        onCancel={() => setOpenConfirmDelete(false)}
        open={openConfrmDelete}
        title={dataDialogDelete.title}
        content={dataDialogDelete.content}
      />

      <DrawAddProduct
        key={`addproduct_${componentKey}`}
        open={openDrawAddProduct}
        onClose={handleClose}
        contractId={dataInfo?.piId}
        data={dataInfo}
        title={intl.formatMessage({ defaultMessage: "Add to cart" })}
        handleSubmit={handleAddProd}
        listProductAdded={listLineState}
        addType={DrawAddExportType.SHOPPINGCART}
      />
      {isSuccessfully && (
        <SnackbarAlert
          severity={isSuccessfully}
          open={!!isSuccessfully}
          onClose={() => setSuccessfully(null)}
          verticalCenter={true}
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
        >
          <div className={classes.snackHeadTitle}>
            <MyTypography
              variant="subtitle2"
              style={{ color: "#ffffff", marginRight: "2px" }}
            >
              {intl.formatMessage({
                defaultMessage: "Added to cart",
                description: "dialog header"
              })}
            </MyTypography>
            <MyTypography variant="body3" style={{ color: "#ffffff" }}>
              {isSuccessfully === "success"
                ? "เพิ่มสินค้าลงตะกร้าสำเร็จ"
                : "เพิ่มสินค้าลงตะกร้าไม่สำเร็จ"}
            </MyTypography>
          </div>
        </SnackbarAlert>
      )}
      <Backdrop open={!!isSuccessfully} className={classes.backDrop} />
    </>
  );
};

export default CartDetailTable;
